import React, { useState, useMemo, useEffect, Fragment } from 'react';
import './InsightSuburbsPage.css';
import { useGeographicUnits } from '../../../hooks/useGeographicUnits';
import { useAuth } from '../../../context/AuthContext';
import SuburbsTable from './SuburbsTable';
import HeatmapPanel from './HeatmapPanel';

import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import templateBg from "../../../assets/images/insight_suburbs_template.png";

import portadaReporte from "../../../assets/images/portada_reporte_invescasa.png";
import pagina1Invescasa from "../../../assets/images/que_es_invescasa_pagina_1.png";
import pagina2Invescasa from "../../../assets/images/que_es_invescasa_pagina_2.png";


/* -----------------------------
   Helpers
--------------------------------*/
function capitalizeWords(str) {
  if (!str) return "—";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}


const indicators = [
  
  { name: "Best suburbs rental yield"},
  { name: "Best Long term growth rates suburbs"
 },
  { name: "Lowest growth suburbs",
},
{ name: "Lowest vacancy rates suburbs",
 },
];



/* -----------------------------
   COMPONENTE PRINCIPAL
--------------------------------*/
export default function InsightSuburbsPage() {
  const { places: cities } = useGeographicUnits("City");
  const { membership } = useAuth();
  const [selected, setSelected] = useState(null);
  const [isActiveMember, setIsActiveMember] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    const status = membership?.status || '';
    const active = String(status).toLowerCase() === 'active';
    setIsActiveMember(active);
  }, [membership]);
  
  const sortedCities = useMemo(() => [...(cities || [])].sort(), [cities]);

  useEffect(() => {
    if (!selected && sortedCities.length > 0) {
      setSelected(sortedCities[0]);
    }
  }, [sortedCities, selected]);


  /* -----------------------------
     Descarga PDF
  --------------------------------*/
const handleDownloadPDF = async () => {

  try{
  setIsGenerating(true);   // <--- activar loader

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "px",
    format: [1920, 1080],
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // ============================
  // Template cargado una vez
  // ============================
  const templateImg = new Image();
  templateImg.src = templateBg;
  templateImg.crossOrigin = "anonymous";
  await new Promise((resolve) => (templateImg.onload = resolve));

  // ============================
  // Portadas
  // ============================
  const portada1 = new Image();
  portada1.src = portadaReporte;
  portada1.crossOrigin = "anonymous";

  const portada2 = new Image();
  portada2.src = pagina1Invescasa;
  portada2.crossOrigin = "anonymous";

  const portada3 = new Image();
  portada3.src = pagina2Invescasa;
  portada3.crossOrigin = "anonymous";

  await Promise.all([
    portada1.decode(),
    portada2.decode(),
    portada3.decode(),
  ]);

  // === Página 1: Portada
  pdf.addImage(portada1, "PNG", 0, 0, pageWidth, pageHeight);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(45);
  pdf.setTextColor("#ffffff");
  pdf.text("Real estate market\ninsight suburbs", 380, 685);
  pdf.addPage();

  // Página 2 y 3: otras portadas
  pdf.addImage(portada2, "PNG", 0, 0, pageWidth, pageHeight);
  pdf.addPage();

  pdf.addImage(portada3, "PNG", 0, 0, pageWidth, pageHeight);
  pdf.addPage();

  // ============================
  // Tabla de Contenidos
  // ============================
  pdf.addImage(templateImg, "PNG", 0, 0, pageWidth, pageHeight);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(125);
  pdf.setTextColor("#ffffff");
  pdf.text("Table of Contents", 450, 145);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(38);
  pdf.setTextColor("#0f385d");
  const startX = 450,
    baseY = 360,
    stepY = 65;


  pdf.text(`1. Real estate market insight suburbs`, startX, baseY);

  indicators.forEach((indicator, index) => {
    const positionY = baseY + stepY * (index + 1); // desplaza la posición visual
    const itemNumber = index + 2; // desplaza el índice numérico
    pdf.text(`${itemNumber}. ${indicator.name}`, startX, positionY);
  });

  // Barra vertical
  const barX = 1550;
  const barWidth = 11;
  const firstItemY = baseY - 38;
  const lastItemY = baseY + (indicators.length) * stepY + 15;
  pdf.setFillColor("#0f385d");
  pdf.rect(barX, firstItemY, barWidth, lastItemY - firstItemY, "F");

  // ============================
  // Funciones auxiliares
  // ============================
  const captureElementAsPng = async (el) => {
    const canvas = await html2canvas(el, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
    });
    return canvas.toDataURL("image/png");
  };

  const addPageHeader = (pageNumber, indicatorName) => {
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(48);
    pdf.setTextColor("#FFFFFF");
    pdf.text("Real estate market insight suburbs", 435, 90);

    pdf.setFontSize(32);
    pdf.text(
      `${indicatorName}`,
      435,
      130
    );

    pdf.setFontSize(130);
    pdf.setTextColor("#000000");
    pdf.text(`${pageNumber}`, 54, 302);
  };

  const addMapPage = async (el, indicatorName, pageNumber) => {
    const png = await captureElementAsPng(el);
    pdf.addPage();
    pdf.addImage(templateImg, "PNG", 0, 0, pageWidth, pageHeight);

    const img = new Image();
    img.src = png;
    await img.decode();

    const width = 1650;
    const height = 750;
    const posX = 200;
    const posY = 250;

    pdf.addImage(png, "PNG", posX, posY, width, height);
    addPageHeader(pageNumber, indicatorName);
  };

  const addTablePage = async (el, indicatorName, pageNumber) => {
    const png = await captureElementAsPng(el);
    pdf.addPage();
    pdf.addImage(templateImg, "PNG", 0, 0, pageWidth, pageHeight);

    const img = new Image();
    img.src = png;
    await img.decode();

    const width = 749;
    const height = 849;
    const posX = 600;
    const posY = 200;

    pdf.addImage(png, "PNG", posX, posY, width, height);
    addPageHeader(pageNumber, indicatorName);
  };

  const heatmap = document.querySelector(".heatmap-container");
      if (heatmap) {
        await addMapPage(heatmap, "Real estate market insight suburbs", 1);}
  // ============================
  // 📄 Contenido por indicador
  // ============================
  let pageCounter = 2;

  for (let i = 0; i < indicators.length; i++) {
    const indicator = indicators[i] ;


    // 🔹 Páginas con tablas
    const tables = document.querySelectorAll(".suburbs-table-container");
    
    const tableEl = tables[i];
    const scrollEl = tableEl.querySelector(".table-scroll-container");
    const prevHeight = scrollEl.style.maxHeight;

    scrollEl.style.maxHeight = "none";
    scrollEl.style.overflow = "visible";

    await addTablePage(tableEl, indicator.name, pageCounter++);

    scrollEl.style.maxHeight = prevHeight || "";
    scrollEl.style.overflow = "auto";
    
  }

  // 🧾 Guardar PDF
  pdf.save("suburbs-report.pdf");
  }catch (err) {
    console.error("Error generating PDF:", err);
  } finally {
    setIsGenerating(false);   // <--- desactivar loader
  }
};

  return (
    <div className="background">
      <div className="panel-modal-container">

        {/* BANNER */}
        <div className="mini-banner">
          <span className="banner-label">Suburbs in</span>
          <div className="tabs">
            {sortedCities.map((item, i) => (
              <Fragment key={item}>
                {i > 0 && <div className="separator" />}
                <button
                  className={`tab-button${selected === item ? ' active' : ''}`}
                  onClick={() => setSelected(item)}
                >
                  {capitalizeWords(item)}
                </button>
              </Fragment>
            ))}
          </div>
        </div>
        
        {/* TITULO + BOTÓN */}
        <div className="main-title">
          <h2 className="main-title__title">Real estate market insight suburbs</h2>
          <button 
            className="download-report"
            onClick={handleDownloadPDF}
            disabled={!isActiveMember || isGenerating}   // <--- se desactiva si no es miembro o si está generando
            style={!isActiveMember ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            aria-label="Download report"
          >
            <span className="icon">
              {isGenerating ? (
                <span className="spinner"></span>   // <--- loader
              ) : (
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              )}
            </span>

            {isGenerating ? "Generating..." : "Download report"}
          </button>

        </div>

        {/* HEATMAP */}
        {selected && (
          <div className="heatmap-container">
            <HeatmapPanel city={selected} />
          </div>
        )}

        {/* TABLAS */}
        {selected && (
          <div className="tables-grid">
            <div className="suburbs-table-container">
              <SuburbsTable
                selectedCity={selected}
                title="Best suburbs rental yield"
                primaryIndicator="rental_yield_pct"
                secondaryIndicator="median_sales_price_last_12_months"
                order="desc"
                isActiveMember={isActiveMember}
              />
            </div>

            <div className="suburbs-table-container">
              <SuburbsTable
                selectedCity={selected}
                title="Best Long term growth rates suburbs"
                primaryIndicator="lt_growth_rate_20y_pct"
                secondaryIndicator="median_sales_price_last_12_months"
                order="desc"
                isActiveMember={isActiveMember}
              />
            </div>

            <div className="suburbs-table-container">
              <SuburbsTable
                selectedCity={selected}
                title="Lowest growth suburbs"
                primaryIndicator="median_price_change_5y_pct"
                secondaryIndicator="median_sales_price_last_12_months"
                order="asc"
                isActiveMember={isActiveMember}
              />
            </div>

            <div className="suburbs-table-container">
              <SuburbsTable
                selectedCity={selected}
                title="Lowest vacancy rates suburbs"
                primaryIndicator="vacancy_rate_sqm"
                secondaryIndicator="median_sales_price_last_12_months"
                order="asc"
                isActiveMember={isActiveMember}
              />
            </div>

          </div>
        )}

      </div>
    </div>
  );
}
